import { z } from 'zod';
import { listQuerySchema, optionalText } from './common';
import type { UserRef } from './users';

/**
 * Coupon codes (reference PC852) and Digital Card (PC1141). The lists were read from the
 * reference (product inventory, appendix 11.7 and the owner's screenshots), the "Add Coupon
 * Code" and "Generate Card" dialogs later (appendix 12.4 and 12.5). "Assign Card" was not
 * seen: its one field follows the list column.
 */

const isoDate = (label: string) => z.string().regex(/^\d{4}-\d{2}-\d{2}$/, `${label} is required`);

/* --------------------------------- Coupons -------------------------------- */

export const COUPON_TYPES = ['Fixed', 'Percent'] as const;
export type CouponType = (typeof COUPON_TYPES)[number];

export const COUPON_USES = ['One-Time Use', 'Multiple Use'] as const;
/** "Coupon Applicable On". */
export const COUPON_APPLICABLE_ON = [
  'SIM',
  'Equipment',
  'Plan',
  'Activation Fee',
  'Total Cost',
  'Gift Card',
] as const;
/** "Coupon Applicable Type"; Recurring asks for a number of months. */
export const COUPON_APPLICABLE_TYPES = ['Recurring', 'One-Time'] as const;
const YES_NO = ['Yes', 'No'] as const;

const whole = (label: string, max: number) =>
  z
    .number({ invalid_type_error: `${label} is required`, required_error: `${label} is required` })
    .int()
    .min(1, `${label} is required`)
    .max(max);

/**
 * The terms of a coupon, as the reference's "Add Coupon Code" dialog asks for them. The
 * terms are stored and listed, and a coupon whose "Coupon Applicable On" is Plan or Total
 * Cost can be used on a plan renewal (server: promotions/redeem.ts). Coupons are not taken
 * at enrollment or on equipment yet.
 */
const couponFields = {
  name: z.string().trim().min(1, 'Coupon Name is required').max(50),
  type: z.enum(COUPON_TYPES, { required_error: 'Select Coupon Type' }),
  /** Fixed: an amount in cents. Percent: whole percent, 1 to 100. */
  value: z.number({ invalid_type_error: 'Coupon Value is required' }).int().min(1).max(10_000_000),
  startDate: isoDate('Coupon Start Date'),
  endDate: isoDate('Coupon End Date'),
  /** "Is tax included?" The reference shows it only in some cases; here it is always asked. */
  taxIncluded: z.enum(YES_NO).default('No'),
  usesPerCoupon: z.enum(COUPON_USES).default('One-Time Use'),
  /** How often a Multiple Use coupon can be used in all. */
  usesCount: z.number().int().min(2, 'Enter how many uses').max(99_999).nullable().default(null),
  applicableOn: z.enum(COUPON_APPLICABLE_ON, { required_error: 'Select Coupon Applicable On' }),
  applicableType: z.enum(COUPON_APPLICABLE_TYPES).default('One-Time'),
  /** "Recurring Month Count", for a Recurring coupon. */
  recurringMonths: z.number().int().min(1).max(99).nullable().default(null),
  /** "Do you want waive off processing fee?" */
  waiveProcessingFee: z.enum(YES_NO).default('No'),
  usesPerCustomer: whole('Uses Per Customer', 99_999),
  status: z.enum(['Active', 'Inactive']).default('Active'),
};

type CouponTerms = {
  type: CouponType;
  value: number;
  startDate: string;
  endDate: string;
  usesPerCoupon: (typeof COUPON_USES)[number];
  usesCount: number | null;
  applicableType: (typeof COUPON_APPLICABLE_TYPES)[number];
  recurringMonths: number | null;
};

const checkCoupon = (v: CouponTerms, ctx: z.RefinementCtx) => {
  if (v.type === 'Percent' && v.value > 100)
    ctx.addIssue({ code: 'custom', path: ['value'], message: 'A percentage is at most 100' });
  if (v.endDate < v.startDate)
    ctx.addIssue({
      code: 'custom',
      path: ['endDate'],
      message: 'Coupon End Date is before the start date',
    });
  if (v.usesPerCoupon === 'Multiple Use' && v.usesCount === null)
    ctx.addIssue({ code: 'custom', path: ['usesCount'], message: 'Enter how many uses' });
  if (v.applicableType === 'Recurring' && v.recurringMonths === null)
    ctx.addIssue({
      code: 'custom',
      path: ['recurringMonths'],
      message: 'Recurring Month Count is required',
    });
};

/** Values that belong to another choice are dropped, so a stored coupon is consistent. */
const tidyCoupon = <T extends CouponTerms>(v: T): T => ({
  ...v,
  usesCount: v.usesPerCoupon === 'Multiple Use' ? v.usesCount : null,
  recurringMonths: v.applicableType === 'Recurring' ? v.recurringMonths : null,
});

export const couponInputSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(1, 'Coupon Code is required')
      .max(30)
      .regex(/^[A-Za-z0-9_-]+$/, 'Use letters, digits, dash and underscore only'),
    ...couponFields,
  })
  .superRefine(checkCoupon)
  .transform(tidyCoupon);
export type CouponInput = z.input<typeof couponInputSchema>;

/**
 * "Bulk Coupon Generation": "Number of Coupons" coupons with the same terms. Assumption: the
 * Coupon Code typed in that mode is the prefix of the generated codes.
 */
export const couponGenerateSchema = z
  .object({
    ...couponFields,
    count: z
      .number({ invalid_type_error: 'Number of Coupons is required' })
      .int()
      .min(1, 'Number of Coupons is required')
      .max(999),
    prefix: z
      .string()
      .trim()
      .max(10)
      .regex(/^[A-Za-z0-9]*$/, 'Use letters and digits only')
      .optional()
      .default(''),
  })
  .superRefine(checkCoupon)
  .transform(tidyCoupon);
export type CouponGenerateInput = z.input<typeof couponGenerateSchema>;

export const COUPON_SOURCES = ['manual', 'upload', 'generated'] as const;
export type CouponSource = (typeof COUPON_SOURCES)[number];

export const couponListQuerySchema = listQuerySchema.extend({
  /** "Enter Coupon Code": exact code, ignoring case. */
  code: optionalText(40),
  source: z.enum(COUPON_SOURCES).optional(),
});

/** Header of the file for "CSV Bulk Upload Coupon", as the reference states it. */
export const COUPON_CSV_COLUMNS = [
  'Coupon Code',
  'Coupon Name',
  'Coupon Type',
  'Coupon Value($)',
  'Start Date',
  'End Date',
  'Tax Included',
  'Coupon will apply on',
  'Waive off processing fee',
  'Uses Per Customer',
  'Uses Per Coupon',
] as const;

export interface CouponDto {
  id: string;
  code: string;
  name: string;
  type: CouponType;
  value: number;
  startDate: string;
  endDate: string;
  taxIncluded: 'Yes' | 'No';
  usesPerCoupon: (typeof COUPON_USES)[number];
  usesCount: number | null;
  applicableOn: (typeof COUPON_APPLICABLE_ON)[number];
  applicableType: (typeof COUPON_APPLICABLE_TYPES)[number];
  recurringMonths: number | null;
  waiveProcessingFee: 'Yes' | 'No';
  usesPerCustomer: number;
  status: 'Active' | 'Inactive';
  source: CouponSource;
  createdBy: UserRef | null;
  createdAt: string;
  updatedBy: UserRef | null;
  updatedAt: string | null;
}

/* ------------------------------- Digital cards ----------------------------- */

/** "Card Type" of the reference's "Generate Card" dialog. */
export const CARD_TYPES = ['VISA', 'MasterCard'] as const;
export type CardType = (typeof CARD_TYPES)[number];

export const cardGenerateSchema = z.object({
  amountCents: z
    .number({ invalid_type_error: 'Amount is required' })
    .int()
    .min(1, 'Amount is required')
    .max(1_000_000),
  cardType: z.enum(CARD_TYPES, { required_error: 'Select Card Type' }),
  /** "No. of Cards": three digits at most. */
  count: z.number({ invalid_type_error: 'No. of Cards is required' }).int().min(1).max(999),
});
export type CardGenerateInput = z.input<typeof cardGenerateSchema>;

export const cardAssignSchema = z.object({
  customerId: z
    .string()
    .trim()
    .regex(/^\d{1,11}$/, 'Customer ID is required'),
});

export const cardListQuerySchema = listQuerySchema.extend({
  cardNumber: optionalText(20),
});

export interface DigitalCardDto {
  id: string;
  /** Sixteen digits. */
  cardNumber: string;
  amountCents: number;
  balanceCents: number;
  cardType: CardType;
  /** The customer the card was given to, once assigned. */
  assignedTo: { id: string; customerId: number; name: string } | null;
  createdBy: UserRef | null;
  createdAt: string;
}
